/**
 * Publish Flow observability service (Phase 5.y: data layer + minimal UI).
 *
 * Reads the Phase 5.x backend artifacts:
 *   GET /publish-flow/{jazelId}/{correlationId}/manifest
 *   GET /publish-flow/{jazelId}/{correlationId}/summary
 *
 * These endpoints expose the per-publish manifest + summary written by
 * `savePublishObservabilityArtifacts` in `ise-srp-back` after every real
 * publish run (NOT preview). Decision-level artifacts for actual publishes
 * are out of scope for this phase.
 *
 * Design parity with `publishPreviewArtifactsService.ts`:
 *   - Two API surfaces per endpoint:
 *       1. Strict (`getPublishFlow*`) — throws on any non-200.
 *       2. Fallback (`tryGetPublishFlow*`) — returns `ArtifactReadResult`.
 *   - Authorization via `Authorization: Bearer <sessionToken>`.
 *   - Same fallback semantics: `notFound` for known absent codes,
 *     `unavailable` for network / 5xx / 404-without-code (route missing).
 *
 * Errors that ALWAYS THROW (both strict and `try*`):
 *   - 401 / 403           — auth/authz failures, surface to user
 *   - 400 MISSING_PATH_PARAM
 *
 * The Phase 5.x handler in
 * `ise/ise-srp-back/amplify/functions/publish_flow_observability_api/handler.ts`
 * is the source of truth for response codes; this service mirrors them
 * locally because the package `@jazel-auto/ise-contracts` does not yet
 * publish the Publish Flow shapes.
 */

import {
  type ArtifactReadResult,
  type PublishObservedStage,
  type PublishStageAvailability,
  type PublishStageCounts,
  type PublishStageSummary as PreviewPublishStageSummary,
  type PublishObservabilityWarning,
  type PublishArtifactPointer,
} from '@/services/publishPreviewArtifactsService'

// ---------------------------------------------------------------------------
// Local mirror types (kept in sync with
// ise/ise-srp-back/src/publish_flow_observability/publishFlowObservabilityArtifacts.ts).
// When `@jazel-auto/ise-contracts` ships these shapes, these mirrors should
// be removed in a single cross-repo PR.
// ---------------------------------------------------------------------------

export type PublishFlowObservabilityLevel = 'aggregate-only' | 'decision-level'

export type PublishFlowStageDetailStatus =
  | 'available'
  | 'empty'
  | 'notConfigured'
  | 'skipped'
  | 'unsupported'
  | 'error'

/**
 * Held-back reason breakdown bucket (mirrors the backend
 * `FlowInventorySyncReasonBucket`). Held-back VINs are never collapsed into a
 * single reason: each distinct `reason.code` is its own bucket so the UI can
 * surface, e.g., "37 archived-returning still blocked" separately from VINs
 * whose feed snapshot was never applied.
 */
export type PublishFlowInventorySyncReasonBucket = {
  code: string
  count: number
  sampleVins?: string[]
}

/**
 * Reconciled Inventory Sync funnel breakdown (mirrors the backend
 * `PublishStageDetailFunnel`). Present only on the `inventorySync` stage. This
 * is the AUTHORITATIVE source for the Inventory Sync card counts — the card
 * never recomputes them from the decision records.
 *
 * Semantics (publish-wide, candidate-anchored reconciliation):
 *   - `intakeCount`     = Feed Preparation pass-through union (P).
 *   - `continuedCount`  = |P ∩ C| (continued as downstream inventory candidates).
 *   - `heldBackCount`   = |P − C| (passed Feed Prep but not in candidate set).
 *   - `admittedOutsideFeedPrepCount` = |C − P| (in candidate set, not feed-derived).
 *   - `handoffCount`    = |C| (vehicles moving to Publish Rules Evaluation).
 *   - `removedCount` / `reconciledOutCount` are prior-inventory actions,
 *     surfaced separately and excluded from the feed funnel math.
 *   - `anomalyCount`    = conflicts / unclassified deltas (never corrupts math).
 */
export type PublishFlowStageDetailFunnel = {
  intakeCount: number
  continuedCount: number
  heldBackCount: number
  admittedOutsideFeedPrepCount: number
  handoffCount: number
  removedCount: number
  reconciledOutCount: number
  anomalyCount: number
  heldBackReasonBuckets?: PublishFlowInventorySyncReasonBucket[]
}

export type PublishFlowStageDetails = {
  status?: PublishFlowStageDetailStatus
  input?: {
    label: string
    count?: number
    artifactKey?: string
    description?: string
  }
  output?: {
    label: string
    count?: number
    artifactKey?: string
    description?: string
  }
  explanation?: string
  reason?: string
  /** Present only for the `inventorySync` stage (reconciled funnel breakdown). */
  funnel?: PublishFlowStageDetailFunnel
}

export type PublishStageSummary = PreviewPublishStageSummary & {
  outcome?: string
  reason?: string
  details?: PublishFlowStageDetails
}

/**
 * Feed Preparation pass-through artifact (direct-account / no-preProcessor
 * runs). Associated with the existing preProcessor / Feed Preparation stage —
 * NOT a new visible stage. Its presence is what makes the Feed Preparation
 * card clickable when preProcessor did not run. The pointers are consumed by
 * the `?mode=passThrough` read API, not fetched directly here.
 */
export type PublishPreProcessorPassThroughArtifact = {
  mode: 'notNeeded'
  reason?: string
  recordCount: number
  parts: PublishArtifactPointer[]
  vinIndex?: PublishArtifactPointer
}

export type PublishFlowObservabilityManifest = {
  version: number
  runType: 'publish'
  id?: string
  runId: string
  jazelId: string
  generatedAt: string
  expiresAt?: string
  retentionClass?: 'preview' | 'publish-history' | 'debug'
  observabilityLevel?: PublishFlowObservabilityLevel
  summary: PublishArtifactPointer
  stageDecisions: Partial<Record<PublishObservedStage, PublishArtifactPointer[]>>
  preProcessorPassThrough?: PublishPreProcessorPassThroughArtifact
  stages: Partial<Record<PublishObservedStage, PublishStageSummary>>
  warnings?: PublishObservabilityWarning[]
}

export type PublishFlowObservabilitySummary = {
  version: number
  runType: 'publish'
  runId: string
  jazelId: string
  generatedAt: string
  observabilityLevel?: PublishFlowObservabilityLevel
  totals: PublishStageCounts
  stages: Partial<Record<PublishObservedStage, PublishStageSummary>>
  warnings?: PublishObservabilityWarning[]
}

// Re-exports for downstream consumers (UI components, tests) so they don't
// need to import from two different services to assemble a render plan.
export type {
  PublishObservedStage,
  PublishStageAvailability,
  PublishStageCounts,
  PublishObservabilityWarning,
  ArtifactReadResult,
}

// ---------------------------------------------------------------------------
// Internal helpers.
// ---------------------------------------------------------------------------

const trimBase = (endpoint: string) => endpoint.replace(/\/+$/g, '')

const authHeaders = (sessionToken: string) => ({
  Accept: 'application/json',
  Authorization: `Bearer ${sessionToken}`,
})

type RawErrorPayload = {
  error?: string
  message?: string
  code?: string
}

type RawArtifactResponse =
  | { ok: true; data: unknown }
  | { ok: false; status: number; payload: RawErrorPayload | null }

async function requestArtifact(input: {
  endpoint: string
  path: string
  sessionToken: string
}): Promise<RawArtifactResponse> {
  const base = trimBase(input.endpoint)
  const url = `${base}${input.path}`
  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(input.sessionToken),
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    return {
      ok: false,
      status: response.status,
      payload: (payload ?? null) as RawErrorPayload | null,
    }
  }
  // Phase 5.x always returns `{ success: true, data: ... }` for 200s.
  // Defensive fallback to the raw body in case a future deployment skips
  // the envelope (matches the Preview service's behavior).
  const data =
    payload && typeof payload === 'object' && 'data' in payload
      ? (payload as { data: unknown }).data
      : payload
  return { ok: true, data }
}

function strictErrorMessage(
  res: { ok: false; status: number; payload: RawErrorPayload | null },
  fallbackPrefix: string,
): string {
  return (
    res.payload?.message ||
    res.payload?.error ||
    `${fallbackPrefix} (${res.status})`
  )
}

/**
 * 4xx codes that ALWAYS throw — auth failures and programmer errors.
 */
const ALWAYS_THROW_CODES = new Set<string>(['MISSING_PATH_PARAM'])

function shouldAlwaysThrow(
  res: { ok: false; status: number; payload: RawErrorPayload | null },
): boolean {
  if (res.status === 401 || res.status === 403) return true
  const code = res.payload?.code
  return Boolean(code && ALWAYS_THROW_CODES.has(code))
}

function unavailableReason(
  res: { ok: false; status: number; payload: RawErrorPayload | null },
): { reason: 'http5xx' | 'route'; message: string } | null {
  if (res.status >= 500) {
    return {
      reason: 'http5xx',
      message: strictErrorMessage(res, 'Publish flow details unavailable'),
    }
  }
  // A 404 with NO recognized code means the route is genuinely missing
  // (older backend deployment). That's "the new endpoint isn't deployed",
  // not "this run has no artifact".
  if (res.status === 404 && !res.payload?.code) {
    return {
      reason: 'route',
      message: strictErrorMessage(res, 'Publish flow details route missing'),
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// Manifest.
// ---------------------------------------------------------------------------

const MANIFEST_NOT_FOUND_CODES = new Set(['PUBLISH_OBSERVABILITY_MANIFEST_NOT_FOUND'])

function manifestPath(jazelId: string, correlationId: string): string {
  return `/publish-flow/${encodeURIComponent(jazelId)}/${encodeURIComponent(correlationId)}/manifest`
}

export async function getPublishFlowManifest(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
}): Promise<PublishFlowObservabilityManifest> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: manifestPath(params.jazelId, params.correlationId),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish flow manifest'))
  }
  return res.data as PublishFlowObservabilityManifest
}

export async function tryGetPublishFlowManifest(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
}): Promise<ArtifactReadResult<PublishFlowObservabilityManifest>> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: manifestPath(params.jazelId, params.correlationId),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }
  if (res.ok) return { status: 'ok', data: res.data as PublishFlowObservabilityManifest }
  if (shouldAlwaysThrow(res)) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish flow manifest'))
  }
  if (
    res.status === 404
    && res.payload?.code
    && MANIFEST_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Publish flow manifest not found'),
    }
  }
  const unavailable = unavailableReason(res)
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }
  throw new Error(strictErrorMessage(res, 'Failed to fetch publish flow manifest'))
}

// ---------------------------------------------------------------------------
// Summary.
// ---------------------------------------------------------------------------

const SUMMARY_NOT_FOUND_CODES = new Set(['PUBLISH_OBSERVABILITY_SUMMARY_NOT_FOUND'])

function summaryPath(jazelId: string, correlationId: string): string {
  return `/publish-flow/${encodeURIComponent(jazelId)}/${encodeURIComponent(correlationId)}/summary`
}

export async function getPublishFlowSummary(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
}): Promise<PublishFlowObservabilitySummary> {
  const res = await requestArtifact({
    endpoint: params.endpoint,
    path: summaryPath(params.jazelId, params.correlationId),
    sessionToken: params.sessionToken,
  })
  if (!res.ok) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish flow summary'))
  }
  return res.data as PublishFlowObservabilitySummary
}

export async function tryGetPublishFlowSummary(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
}): Promise<ArtifactReadResult<PublishFlowObservabilitySummary>> {
  let res: RawArtifactResponse
  try {
    res = await requestArtifact({
      endpoint: params.endpoint,
      path: summaryPath(params.jazelId, params.correlationId),
      sessionToken: params.sessionToken,
    })
  } catch (error) {
    return {
      status: 'unavailable',
      reason: 'network',
      message: error instanceof Error ? error.message : 'Network request failed',
    }
  }
  if (res.ok) return { status: 'ok', data: res.data as PublishFlowObservabilitySummary }
  if (shouldAlwaysThrow(res)) {
    throw new Error(strictErrorMessage(res, 'Failed to fetch publish flow summary'))
  }
  if (
    res.status === 404
    && res.payload?.code
    && SUMMARY_NOT_FOUND_CODES.has(res.payload.code)
  ) {
    return {
      status: 'notFound',
      code: res.payload.code,
      message: strictErrorMessage(res, 'Publish flow summary not found'),
    }
  }
  const unavailable = unavailableReason(res)
  if (unavailable) {
    return { status: 'unavailable', ...unavailable }
  }
  throw new Error(strictErrorMessage(res, 'Failed to fetch publish flow summary'))
}

// ---------------------------------------------------------------------------
// Convenience aggregator.
// ---------------------------------------------------------------------------

export type PublishFlowObservabilityFetch = {
  manifest: ArtifactReadResult<PublishFlowObservabilityManifest>
  summary: ArtifactReadResult<PublishFlowObservabilitySummary>
}

/**
 * Fetch manifest + summary in parallel using the `try*` variants so a
 * single failure never poisons the page. The two probes are independent;
 * the caller decides whether to render based on `manifest.status === 'ok'
 * && summary.status === 'ok'`.
 *
 * `try*` rules still hold here: programmer errors (401/403/400 with
 * `MISSING_PATH_PARAM`) propagate as thrown errors. `Promise.allSettled`
 * captures them so the caller can react uniformly via `errors`.
 */
export async function fetchPublishFlowObservability(params: {
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
}): Promise<PublishFlowObservabilityFetch> {
  const [manifestRes, summaryRes] = await Promise.allSettled([
    tryGetPublishFlowManifest(params),
    tryGetPublishFlowSummary(params),
  ])

  const manifest =
    manifestRes.status === 'fulfilled'
      ? manifestRes.value
      : ({
          status: 'unavailable' as const,
          reason: 'network' as const,
          message:
            manifestRes.reason instanceof Error
              ? manifestRes.reason.message
              : String(manifestRes.reason ?? 'Network request failed'),
        })

  const summary =
    summaryRes.status === 'fulfilled'
      ? summaryRes.value
      : ({
          status: 'unavailable' as const,
          reason: 'network' as const,
          message:
            summaryRes.reason instanceof Error
              ? summaryRes.reason.message
              : String(summaryRes.reason ?? 'Network request failed'),
        })

  return { manifest, summary }
}
